/* EDIMI — treść stron prawnych, budowana z namespace „legal" (PL = wersja wiążąca,
   EN = tłumaczenie kurtuazyjne). Dane identyfikacyjne usługodawcy/administratora
   (JDG Dominik Łupicki) uzupełnione. Sekcje są budowane locale-aware przez t()
   (frontend/src/lib/i18n.tsx).

   Trzy dokumenty, jeden layout (LegalPage): Regulamin (16 §), Polityka prywatności (10 §)
   i „Zwroty i anulowanie" (6 §). Ta trzecia strona jest CZĘŚCIĄ Regulaminu — Stripe wymaga
   jednoznacznej, osobno linkowalnej polityki zwrotów, a wciśnięcie jej w §12 Regulaminu
   sprawiłoby, że klient musi czytać całą umowę, żeby dowiedzieć się, jak anulować.

   Numeracja §§ jest cytowana w treści (np. §11 przy odstąpieniu, §13 przy reklamacjach)
   i w `app/billing/consents.py`. Przy dodawaniu sekcji sprawdź odsyłacze w locales. */
import type { TFunc } from '../lib/i18n'

export type Block =
  | { t: 'p'; v: string }
  | { t: 'h3'; v: string }
  | { t: 'ul'; v: string[] }
  | { t: 'note'; v: string }
  | { t: 'deftable'; rows: [string, string][] }

export interface LegalSection {
  id: string
  num: string
  title: string
  blocks: Block[]
}

export function buildRegulamin(t: TFunc): LegalSection[] {
  return [
    { id: 'r1', num: '1', title: t('legal.regulamin.r1.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r1.p1') },
      { t: 'p', v: t('legal.regulamin.r1.p2') },
      { t: 'p', v: t('legal.regulamin.r1.p3') },
    ]},
    { id: 'r2', num: '2', title: t('legal.regulamin.r2.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r2.p1') },
      { t: 'deftable', rows: [
        [t('legal.regulamin.r2.row1k'), t('legal.regulamin.r2.row1v')],
        [t('legal.regulamin.r2.row2k'), t('legal.regulamin.r2.row2v')],
        [t('legal.regulamin.r2.row3k'), t('legal.regulamin.r2.row3v')],
        [t('legal.regulamin.r2.row4k'), t('legal.regulamin.r2.row4v')],
        [t('legal.regulamin.r2.row5k'), t('legal.regulamin.r2.row5v')],
        [t('legal.regulamin.r2.row6k'), t('legal.regulamin.r2.row6v')],
        [t('legal.regulamin.r2.row7k'), t('legal.regulamin.r2.row7v')],
        [t('legal.regulamin.r2.row8k'), t('legal.regulamin.r2.row8v')],
        [t('legal.regulamin.r2.row9k'), t('legal.regulamin.r2.row9v')],
      ]},
    ]},
    { id: 'r3', num: '3', title: t('legal.regulamin.r3.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r3.p1') },
      { t: 'h3', v: t('legal.regulamin.r3.h1') },
      { t: 'ul', v: [
        t('legal.regulamin.r3.ul1'),
        t('legal.regulamin.r3.ul2'),
        t('legal.regulamin.r3.ul3'),
        t('legal.regulamin.r3.ul4'),
      ]},
      { t: 'p', v: t('legal.regulamin.r3.p2') },
    ]},
    { id: 'r4', num: '4', title: t('legal.regulamin.r4.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r4.p1') },
      { t: 'p', v: t('legal.regulamin.r4.p2') },
      { t: 'p', v: t('legal.regulamin.r4.p3') },
    ]},
    { id: 'r5', num: '5', title: t('legal.regulamin.r5.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r5.p1') },
      { t: 'p', v: t('legal.regulamin.r5.p2') },
      { t: 'p', v: t('legal.regulamin.r5.p3') },
      { t: 'p', v: t('legal.regulamin.r5.p4') },
      { t: 'note', v: t('legal.regulamin.r5.note') },
    ]},
    { id: 'r6', num: '6', title: t('legal.regulamin.r6.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r6.p1') },
      { t: 'p', v: t('legal.regulamin.r6.p2') },
      { t: 'p', v: t('legal.regulamin.r6.p3') },
    ]},
    { id: 'r7', num: '7', title: t('legal.regulamin.r7.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r7.p1') },
      { t: 'p', v: t('legal.regulamin.r7.p2') },
      { t: 'h3', v: t('legal.regulamin.r7.h1') },
      { t: 'ul', v: [
        t('legal.regulamin.r7.ul1'),
        t('legal.regulamin.r7.ul2'),
        t('legal.regulamin.r7.ul3'),
        t('legal.regulamin.r7.ul4'),
      ]},
      { t: 'p', v: t('legal.regulamin.r7.p3') },
      { t: 'note', v: t('legal.regulamin.r7.note') },
      { t: 'p', v: t('legal.regulamin.r7.p4') },
    ]},
    { id: 'r8', num: '8', title: t('legal.regulamin.r8.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r8.p1') },
      { t: 'p', v: t('legal.regulamin.r8.p2') },
      { t: 'p', v: t('legal.regulamin.r8.p3') },
      { t: 'p', v: t('legal.regulamin.r8.p4') },
      { t: 'p', v: t('legal.regulamin.r8.p5') },
    ]},
    { id: 'r9', num: '9', title: t('legal.regulamin.r9.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r9.p1') },
      { t: 'ul', v: [
        t('legal.regulamin.r9.ul1'),
        t('legal.regulamin.r9.ul2'),
        t('legal.regulamin.r9.ul3'),
        t('legal.regulamin.r9.ul4'),
        t('legal.regulamin.r9.ul5'),
      ]},
      { t: 'p', v: t('legal.regulamin.r9.p2') },
      { t: 'p', v: t('legal.regulamin.r9.p3') },
      { t: 'note', v: t('legal.regulamin.r9.note') },
    ]},
    { id: 'r10', num: '10', title: t('legal.regulamin.r10.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r10.p1') },
      { t: 'p', v: t('legal.regulamin.r10.p2') },
      { t: 'p', v: t('legal.regulamin.r10.p3') },
      { t: 'p', v: t('legal.regulamin.r10.p4') },
      { t: 'p', v: t('legal.regulamin.r10.p5') },
    ]},
    { id: 'r11', num: '11', title: t('legal.regulamin.r11.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r11.p1') },
      { t: 'p', v: t('legal.regulamin.r11.p2') },
      { t: 'ul', v: [
        t('legal.regulamin.r11.ul1'),
        t('legal.regulamin.r11.ul2'),
      ]},
      { t: 'p', v: t('legal.regulamin.r11.p3') },
      { t: 'p', v: t('legal.regulamin.r11.p4') },
      { t: 'note', v: t('legal.regulamin.r11.note') },
    ]},
    { id: 'r12', num: '12', title: t('legal.regulamin.r12.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r12.p1') },
      { t: 'p', v: t('legal.regulamin.r12.p2') },
      { t: 'p', v: t('legal.regulamin.r12.p3') },
    ]},
    { id: 'r13', num: '13', title: t('legal.regulamin.r13.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r13.p1') },
      { t: 'p', v: t('legal.regulamin.r13.p2') },
      { t: 'p', v: t('legal.regulamin.r13.p3') },
    ]},
    { id: 'r14', num: '14', title: t('legal.regulamin.r14.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r14.p1') },
      { t: 'p', v: t('legal.regulamin.r14.p2') },
      { t: 'ul', v: [
        t('legal.regulamin.r14.ul1'),
        t('legal.regulamin.r14.ul2'),
        t('legal.regulamin.r14.ul3'),
        t('legal.regulamin.r14.ul4'),
      ]},
      { t: 'p', v: t('legal.regulamin.r14.p3') },
      { t: 'note', v: t('legal.regulamin.r14.note') },
    ]},
    { id: 'r15', num: '15', title: t('legal.regulamin.r15.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r15.p1') },
      { t: 'p', v: t('legal.regulamin.r15.p2') },
      { t: 'p', v: t('legal.regulamin.r15.p3') },
    ]},
    { id: 'r16', num: '16', title: t('legal.regulamin.r16.title'), blocks: [
      { t: 'p', v: t('legal.regulamin.r16.p1') },
      { t: 'p', v: t('legal.regulamin.r16.p2') },
      { t: 'p', v: t('legal.regulamin.r16.p3') },
    ]},
  ]
}

export function buildPolityka(t: TFunc): LegalSection[] {
  return [
    { id: 'p1', num: '1', title: t('legal.polityka.p1.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p1.p1') },
      { t: 'p', v: t('legal.polityka.p1.p2') },
    ]},
    { id: 'p2', num: '2', title: t('legal.polityka.p2.title'), blocks: [
      { t: 'ul', v: [
        t('legal.polityka.p2.ul1'),
        t('legal.polityka.p2.ul2'),
        t('legal.polityka.p2.ul3'),
        t('legal.polityka.p2.ul4'),
        t('legal.polityka.p2.ul5'),
        t('legal.polityka.p2.ul6'),
      ]},
    ]},
    { id: 'p3', num: '3', title: t('legal.polityka.p3.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p3.p1') },
      { t: 'deftable', rows: [
        [t('legal.polityka.p3.row1k'), t('legal.polityka.p3.row1v')],
        [t('legal.polityka.p3.row2k'), t('legal.polityka.p3.row2v')],
        [t('legal.polityka.p3.row3k'), t('legal.polityka.p3.row3v')],
        [t('legal.polityka.p3.row4k'), t('legal.polityka.p3.row4v')],
        [t('legal.polityka.p3.row5k'), t('legal.polityka.p3.row5v')],
        [t('legal.polityka.p3.row7k'), t('legal.polityka.p3.row7v')],
      ]},
    ]},
    { id: 'p4', num: '4', title: t('legal.polityka.p4.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p4.p1') },
      { t: 'note', v: t('legal.polityka.p4.note') },
    ]},
    { id: 'p5', num: '5', title: t('legal.polityka.p5.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p5.p1') },
      { t: 'deftable', rows: [
        [t('legal.polityka.p5.row1k'), t('legal.polityka.p5.row1v')],
        [t('legal.polityka.p5.row2k'), t('legal.polityka.p5.row2v')],
        [t('legal.polityka.p5.row3k'), t('legal.polityka.p5.row3v')],
        [t('legal.polityka.p5.row4k'), t('legal.polityka.p5.row4v')],
        [t('legal.polityka.p5.row5k'), t('legal.polityka.p5.row5v')],
        [t('legal.polityka.p5.row6k'), t('legal.polityka.p5.row6v')],
        [t('legal.polityka.p5.row7k'), t('legal.polityka.p5.row7v')],
        [t('legal.polityka.p5.row8k'), t('legal.polityka.p5.row8v')],
        [t('legal.polityka.p5.row9k'), t('legal.polityka.p5.row9v')],
      ]},
      { t: 'note', v: t('legal.polityka.p5.note') },
    ]},
    { id: 'p6', num: '6', title: t('legal.polityka.p6.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p6.p1') },
      { t: 'p', v: t('legal.polityka.p6.p2') },
    ]},
    { id: 'p7', num: '7', title: t('legal.polityka.p7.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p7.p1') },
      { t: 'p', v: t('legal.polityka.p7.p2') },
      { t: 'p', v: t('legal.polityka.p7.p3') },
    ]},
    { id: 'p8', num: '8', title: t('legal.polityka.p8.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p8.p1') },
      { t: 'p', v: t('legal.polityka.p8.p2') },
    ]},
    { id: 'p9', num: '9', title: t('legal.polityka.p9.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p9.p1') },
      { t: 'ul', v: [
        t('legal.polityka.p9.ul1'),
        t('legal.polityka.p9.ul2'),
        t('legal.polityka.p9.ul3'),
        t('legal.polityka.p9.ul4'),
      ]},
      { t: 'note', v: t('legal.polityka.p9.note') },
    ]},
    { id: 'p10', num: '10', title: t('legal.polityka.p10.title'), blocks: [
      { t: 'p', v: t('legal.polityka.p10.p1') },
      { t: 'p', v: t('legal.polityka.p10.p2') },
    ]},
  ]
}

export function buildZwroty(t: TFunc): LegalSection[] {
  return [
    { id: 'z1', num: '1', title: t('legal.zwroty.z1.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z1.p1') },
      { t: 'p', v: t('legal.zwroty.z1.p2') },
      { t: 'note', v: t('legal.zwroty.z1.note') },
    ]},
    { id: 'z2', num: '2', title: t('legal.zwroty.z2.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z2.p1') },
      { t: 'p', v: t('legal.zwroty.z2.p2') },
      { t: 'p', v: t('legal.zwroty.z2.p3') },
    ]},
    { id: 'z3', num: '3', title: t('legal.zwroty.z3.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z3.p1') },
      { t: 'p', v: t('legal.zwroty.z3.p2') },
      { t: 'h3', v: t('legal.zwroty.z3.h1') },
      { t: 'ul', v: [
        t('legal.zwroty.z3.ul1'),
        t('legal.zwroty.z3.ul2'),
        t('legal.zwroty.z3.ul3'),
      ]},
      { t: 'note', v: t('legal.zwroty.z3.note') },
    ]},
    { id: 'z4', num: '4', title: t('legal.zwroty.z4.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z4.p1') },
      { t: 'p', v: t('legal.zwroty.z4.p2') },
      { t: 'p', v: t('legal.zwroty.z4.p3') },
    ]},
    { id: 'z5', num: '5', title: t('legal.zwroty.z5.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z5.p1') },
      { t: 'p', v: t('legal.zwroty.z5.p2') },
      // The withdrawal exception is rendered as a NOTE, not buried as body text:
      // it is the one place these terms do not promise an automatic refund.
      { t: 'note', v: t('legal.zwroty.z5.p4') },
      { t: 'p', v: t('legal.zwroty.z5.p3') },
    ]},
    { id: 'z6', num: '6', title: t('legal.zwroty.z6.title'), blocks: [
      { t: 'p', v: t('legal.zwroty.z6.p1') },
      { t: 'p', v: t('legal.zwroty.z6.p2') },
      { t: 'p', v: t('legal.zwroty.z6.p3') },
    ]},
  ]
}
